import { useEffect, useMemo, useRef, useState } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHeader from '@/components/feature/PageHeader';
import SegmentedControl from '@/components/base/SegmentedControl';
import SelectField from '@/components/base/SelectField';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import NewsCard from '@/components/feature/NewsCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { newsItems } from '@/mocks/news';

const sectionOptions = [
  { label: '全部', value: '全部' },
  { label: '最新', value: '最新' },
  { label: '热门', value: '热门' },
  { label: '专题报道', value: '专题报道' },
];

const rangeOptions = [
  { label: '全部时间', value: 'all' },
  { label: '最近一周', value: 'week' },
  { label: '最近一月', value: 'month' },
];

const sortOptions = [
  { label: '最新发布', value: 'latest' },
  { label: '最多阅读', value: 'hot' },
];

const NOW = new Date('2026-10-17T00:00:00');
const DAY = 86400000;
const PAGE_SIZE = 6;

export default function NewsCategoryPage() {
  usePageMeta({
    title: '动漫资讯 - 琉璃神社',
    description:
      '琉璃神社动漫资讯分类，汇总新番定档、制作动态、声优活动、展会与产业观察，支持按时间范围筛选与热度排序。',
    keywords: '动漫资讯,新番情报,动画制作,声优活动,动漫展会,琉璃神社',
  });

  const [section, setSection] = useState('全部');
  const [range, setRange] = useState('all');
  const [sort, setSort] = useState('latest');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const withinRange = (date: string) => {
      if (range === 'all') return true;
      const diff = (NOW.getTime() - new Date(`${date}T00:00:00`).getTime()) / DAY;
      return range === 'week' ? diff <= 7 : diff <= 30;
    };

    return newsItems
      .filter((item) => (section === '全部' ? true : item.section === section))
      .filter((item) => withinRange(item.date))
      .sort((a, b) => {
        if (sort === 'hot') return b.views - a.views;
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
  }, [section, range, sort]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [section, range, sort]);

  const shown = filtered.slice(0, visible);
  const currentPage = Math.max(1, Math.ceil(visible / PAGE_SIZE));

  const resetAll = () => {
    setSection('全部');
    setRange('all');
    setSort('latest');
  };

  const changePage = (page: number) => {
    setVisible(page * PAGE_SIZE);
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <SiteLayout>
      <PageHeader
        eyebrow="News"
        title="动漫资讯"
        description="追踪动画、漫画与二次元周边的最新动态。按分类、时间范围与热度自由组合，找到你关心的那一条。"
        breadcrumb={[{ label: '动漫资讯' }]}
      />

      <div className="shell py-8 md:py-12">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <SegmentedControl
            options={sectionOptions}
            value={section}
            onChange={setSection}
            ariaLabel="资讯子分类"
          />
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setFilterOpen((prev) => !prev)}
              className="md:hidden inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md border border-background-200 text-xs text-foreground-700 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-filter-3-line w-4 h-4 flex items-center justify-center"></i>
              筛选与排序
              <i className={`ri-arrow-down-s-line w-3.5 h-3.5 flex items-center justify-center transition-transform ${filterOpen ? 'rotate-180' : ''}`}></i>
            </button>
            <span className="text-xs text-secondary-500 whitespace-nowrap">
              共 <span className="text-foreground-900 font-label">{filtered.length}</span> 条资讯
            </span>
          </div>
        </div>

        <div
          className={`${filterOpen ? 'flex' : 'hidden'} md:flex flex-col sm:flex-row sm:items-end gap-4 mt-4 p-4 rounded-lg bg-background-100/70 border border-background-200/70 md:border-0 md:bg-transparent md:p-0 md:mt-5`}
        >
          <SegmentedControl
            options={rangeOptions}
            value={range}
            onChange={setRange}
            ariaLabel="时间范围筛选"
          />
          <SelectField
            label="排序方式"
            value={sort}
            options={sortOptions}
            onChange={setSort}
            icon="ri-sort-desc"
          />
          <button
            type="button"
            onClick={resetAll}
            className="inline-flex items-center gap-1.5 h-10 px-3 text-xs text-secondary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line w-3.5 h-3.5 flex items-center justify-center"></i>
            重置筛选
          </button>
        </div>

        <div ref={listRef} className="mt-7 scroll-mt-24">
          {shown.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {shown.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>

              {visible < filtered.length && (
                <div className="mt-8 flex flex-col items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 h-11 px-7 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    加载更多
                    <i className="ri-arrow-down-line w-4 h-4 flex items-center justify-center"></i>
                  </button>
                  <span className="text-[11px] text-secondary-400 font-label">
                    已显示 {shown.length} / {filtered.length}
                  </span>
                </div>
              )}

              <Pagination
                total={filtered.length}
                pageSize={PAGE_SIZE}
                page={Math.min(currentPage, Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)))}
                onChange={changePage}
              />
            </>
          ) : (
            <EmptyState
              icon="ri-newspaper-line"
              title="暂时没有符合条件的资讯"
              description="试试放宽时间范围，或切换到其他子分类。新的内容每周都会更新。"
              actionLabel="重置筛选条件"
              onAction={resetAll}
            />
          )}
        </div>
      </div>
    </SiteLayout>
  );
}